import { ClosableDialog } from 'components/controls/dialogs'
import { InventoryCommentsAndLocationsTable } from 'components/domain/onboarding/InventoryCommentsAndLocationsTable'
import { InventoryPriceTable } from 'components/domain/onboarding/InventoryPriceTable'
import { Button } from 'components/facets'
import {
  cardtraderGameAlignmentConfirm,
  cardtraderGameAlignmentStart,
  cardtraderGameAlignmentStatus,
} from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { CardtraderSkippedListing } from 'lib/hooks/useCardtraderAlignmentStatus'
import { useFetchCardTraderStock } from 'lib/hooks/useFetchCardTraderStock'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import { useTranslation } from 'react-i18next'
import {
  convertInventoryArticleToCardTraderStockToSave,
  GAME_NAMES,
  InventoryArticle,
  mergeReconciledArticles,
} from 'shared'

interface CardtraderGameSyncDialogProps {
  open: boolean
  onClose: () => void
  // From the announcement link. With nothing running, opening the dialog starts it.
  requestedIdGame?: number
}

const POLL_INTERVAL_MS = 5000

type Step = 'prices' | 'comments'

export const CardtraderGameSyncDialog = ({
  open,
  onClose,
  requestedIdGame,
}: CardtraderGameSyncDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const { handle } = useErrorHandler()
  const { enqueueSnackbar } = useSnackbar()
  const { toInventoryArticles } = useFetchCardTraderStock()

  const [status, setStatus] = useState<string | null>(null)
  const [idGame, setIdGame] = useState<number | null>(null)
  const [step, setStep] = useState<Step>('prices')
  const [articles, setArticles] = useState<InventoryArticle[]>([])
  const [unknownListings, setUnknownListings] = useState<CardtraderSkippedListing[]>([])
  const [submitting, setSubmitting] = useState(false)
  // loadStatus runs again when the card data settles, and two overlapping runs
  // would both find nothing in flight and start a second alignment.
  const startRequested = useRef(false)

  const loadStatus = useCallback(async () => {
    try {
      let payload = await cardtraderGameAlignmentStatus(requestedIdGame)

      // What the status reports may be about another game — one left failed, or
      // still being reviewed — and it has nothing to say about the import the
      // user just asked for.
      const isAboutAnotherGame =
        requestedIdGame !== undefined &&
        payload.idGame !== null &&
        payload.idGame !== requestedIdGame

      // Opening the dialog is the moment the user committed to the import.
      if (
        (payload.status === 'cancelled' || isAboutAnotherGame) &&
        requestedIdGame !== undefined &&
        !startRequested.current
      ) {
        startRequested.current = true
        await cardtraderGameAlignmentStart(requestedIdGame)
        payload = await cardtraderGameAlignmentStatus(requestedIdGame)
      }

      // The articles first: the review tables build their working copy when they
      // mount, and mounting them on an empty list leaves them empty for good.
      if (payload.status === 'ready_for_review') {
        const inventory = payload.inventory ?? []
        const inventoryArticles = toInventoryArticles(inventory)
        setArticles(inventoryArticles)
        // Rails is the only judge of what could not be placed. A card this client
        // has no data for is not reported here: with the card data up to date
        // that does not happen.
        setUnknownListings(payload.skippedListings ?? [])
      }

      setStatus(payload.status)
      setIdGame(payload.idGame ?? null)
    } catch (error) {
      handle(error)
    }
    // handle comes from a hook that rebuilds it on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toInventoryArticles, requestedIdGame])

  useEffect(() => {
    if (!open) startRequested.current = false
    if (open) loadStatus()
  }, [open, loadStatus])

  // The fetch takes minutes, so the dialog keeps asking until it has something.
  useEffect(() => {
    if (!open || status !== 'fetching') return

    const interval = setInterval(loadStatus, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [open, status, loadStatus])

  const gameName = idGame === null ? '' : GAME_NAMES[idGame] ?? ''

  // The failed run left the game free to be asked for again, so the way back in
  // is here rather than a trip to Sales Channels.
  const retryImport = async () => {
    const gameToRetry = requestedIdGame ?? idGame
    if (gameToRetry === null || submitting) return

    setSubmitting(true)
    try {
      await cardtraderGameAlignmentStart(gameToRetry)
      setStatus('fetching')
    } catch (error) {
      handle(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmImport = async (updatedInventory: InventoryArticle[]) => {
    if (submitting) return

    setSubmitting(true)
    try {
      await cardtraderGameAlignmentConfirm(
        convertInventoryArticleToCardTraderStockToSave(updatedInventory)
      )
      enqueueSnackbar(t('connectCardtrader.gameSync.snackbar.imported', { game: gameName }), {
        variant: 'success',
      })
      onClose()
    } catch (error) {
      handle(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ClosableDialog maxWidth="lg" open={open} onClose={onClose} fullWidth>
      <h3>{t('connectCardtrader.gameSync.title', { game: gameName })}</h3>

      {status === 'fetching' && (
        <p>{t('connectCardtrader.gameSync.fetching', { game: gameName })}</p>
      )}

      {status === 'importing' && (
        <p>{t('connectCardtrader.gameSync.snackbar.imported', { game: gameName })}</p>
      )}

      {status === 'already_imported' && (
        <p>{t('connectCardtrader.gameSync.alreadyImported', { game: gameName })}</p>
      )}

      {status === 'failed' && (
        <>
          <p>{t('connectCardtrader.gameSync.failed', { game: gameName })}</p>
          <Button variant="contained" color="primary" disabled={submitting} onClick={retryImport}>
            {t('connectCardtrader.gameSync.retry')}
          </Button>
        </>
      )}

      {status === 'ready_for_review' && (
        <>
          {step === 'prices' ? (
            <>
              <p className="fs-6">
                {t('connectCardtrader.gameSync.explanation', { game: gameName })}
              </p>

              {unknownListings.length > 0 && (
                <p className="fs-6">
                  {t('connectCardtrader.gameSync.unknownListings', {
                    count: unknownListings.length,
                  })}{' '}
                  <CSVLink
                    data={unknownListings}
                    filename="cardtrader-not-imported.csv"
                    enclosingCharacter={'"'}
                  >
                    {t('connectCardtrader.gameSync.downloadUnknownListings')}
                  </CSVLink>
                </p>
              )}

              <p className="fs-6">
                {t('connectCardtrader.gameSync.received', { count: articles.length })}
              </p>
              <div className="inventory-price-table mb-5" style={{ minHeight: '300px' }}>
                <InventoryPriceTable
                  fetchedStock={articles}
                  confirmLabel={t('connectCardtrader.gameSync.continueToComments')}
                  onConfirm={(updatedInventory) => {
                    setArticles(updatedInventory)
                    setStep('comments')
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div className="inventory-comments-table mb-5" style={{ minHeight: '300px' }}>
                <InventoryCommentsAndLocationsTable
                  withPageLayout={false}
                  fetchedStock={articles}
                  onConfirm={(reconciled) =>
                    confirmImport(mergeReconciledArticles(articles, reconciled))
                  }
                />
              </div>
            </>
          )}
        </>
      )}
    </ClosableDialog>
  )
}
